// Colic Coverage Review (approved HTML: 06-Colic-Coverage-Review.html).
//
// Priority focus, identity row, customer tabs (Colic Coverage active),
// coverage record card, attention list, enrollment progress steps,
// horses-on-account sidebar, related subscription, coverage-impact
// warning, program information links, next-customer preview, Finish and
// Next footer.
//
// Program copy follows reference_100x_equine_colic_program_terms: 30-day
// qualifying period and separate confirmation of application / written
// acceptance / qualifying period / active coverage. Never claims coverage
// based on purchase alone.
//
// R9 companion-state wiring: the coverage-grid is wrapped in the shared
// DataBoundary. Signals are injected via `companionSignals` so the
// runtime wires the real services/customer-coverage + account-workflow
// outcomes without this file branching on their shapes. The Default
// state visual is unchanged.
//
// TODO(runtime): wire actions to
//   - services/customer-coverage (proposed - TODO runtime: no HTTP read
//     endpoint yet; store is at services/customer-coverage/store.mjs)
//   - dist/account-workflows.applications (confirmed in
//     dist/account-workflows.mjs seeds Sarah+Duke "Needs information" with
//     missing fields "Horse date of birth", "Completed enrollment checks")
//   - services/horse-timeline for the horse-profile hand-off
//
// W32 (2026-10-20) real-contact wiring: on a real HubSpot session
// (useReviewIdentity().isPreview === false) the screen renders the real
// identity and the real horses on the contact (GET /v1/contacts/:id/
// horses, each with "No coverage record"). Enrollment controls stay
// visible but inert ("Paused by the live-write gate"). The DUKE_COVERAGE
// / SARAH / MICHAEL fixture is reachable ONLY in preview.
//
// W35 (2026-10-20): the real session reads GET /v1/contacts/:id/
// colic-coverage through the shared relative-fetch reader (the older
// runtime.signed read never fired in prod: no VITE_RUNTIME_API_BASE_URL,
// MEASURED in the W30 audit row 6) and reads the route's real key
// `coverages` (routes/colic-coverage.mjs line 126; the screen used to
// expect `records`). W34 composed the HubSpot sink so the route is 200
// `{result:"SUCCESS", coverages:[]}` for Jodi, Karla and Tere (MEASURED
// by W34 against the local api-server; 0 colic_coverage objects exist
// portal-wide). Empty renders "No colic coverage enrolled yet"; the four
// enrollment steps render as not recorded; the horses list is unchanged.
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { PriorityFocus } from '../_shared/PriorityFocus.js';
import { IdentityRow } from '../_shared/IdentityRow.js';
import { CustomerTabs } from '../_shared/CustomerTabs.js';
import { FileShortcuts } from '../_shared/FileShortcuts.js';
import { UpNext } from '../_shared/UpNext.js';
import { ActionFooter } from '../_shared/ActionFooter.js';
import { DataBoundary, type BoundaryState } from '../_shared/DataBoundary.js';
import { useReviewIdentity } from '../_shared/useReviewIdentity.js';
import { useContactHorses } from '../_shared/useContactCollections.js';
import {
  resolveCompanionState,
  type ScreenCompanionSignals,
} from '../_shared/useScreenCompanionState.js';
import { useActiveCallSignal, mergeMidCallSignal } from '../_shared/useActiveCallSignal.js';
import { CoverageRecord } from './components/CoverageRecord.js';
import { AttentionList } from './components/AttentionList.js';
import { EnrollmentSteps } from './components/EnrollmentSteps.js';
import { CoverageSidebar } from './components/CoverageSidebar.js';
import { COVERAGE_HORSES, DUKE_COVERAGE, type EnrollmentStep } from './mockData.js';
import { SARAH } from '../../data/mockData.js';
import { useContactJsonRead, useSubscriptionSummary, type ContactReadResult } from '../_shared/useSubscriptionSummary.js';
import {
  describeSubscriptionCardError,
  subscriptionCardState,
  SUBSCRIPTION_CARD_ERROR_TITLE,
  SUBSCRIPTION_CARD_LOADING,
  SUBSCRIPTION_CARD_NONE,
} from '../_shared/subscriptionCard.js';
import './styles.css';
import './app.css';

export interface ColicCoverageReviewScreenProps {
  /** R9 test seam for the six companion states. */
  companionSignals?: ScreenCompanionSignals;
}

// ---------------------------------------------------------------------------
// W35: GET /v1/contacts/:id/colic-coverage
//   { result:'SUCCESS', contactId, coverages:[{coverageId, properties,
//     horses:[{horseId, properties}]}] }   (READ routes/colic-coverage.mjs)
// ---------------------------------------------------------------------------

export interface ColicCoverageHorseRecord {
  horseId: string;
  properties: Record<string, unknown>;
}

export interface ColicCoverageRecord {
  coverageId: string;
  properties: Record<string, unknown>;
  horses: ColicCoverageHorseRecord[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Pure projection of the route body; null when `coverages` is not an array. Exported for tests. */
export function projectColicCoverages(body: unknown): ColicCoverageRecord[] | null {
  if (!isRecord(body)) return null;
  const list = body.coverages;
  if (!Array.isArray(list)) return null;
  return list.filter(isRecord).map((raw) => ({
    coverageId: typeof raw.coverageId === 'string' || typeof raw.coverageId === 'number' ? String(raw.coverageId) : '',
    properties: isRecord(raw.properties) ? raw.properties : {},
    horses: Array.isArray(raw.horses)
      ? raw.horses.filter(isRecord).map((h) => ({
          horseId: typeof h.horseId === 'string' || typeof h.horseId === 'number' ? String(h.horseId) : '',
          properties: isRecord(h.properties) ? h.properties : {},
        }))
      : [],
  }));
}

export function useColicCoverage(customerId: string | null | undefined): ContactReadResult<ColicCoverageRecord[]> {
  return useContactJsonRead<ColicCoverageRecord[]>(customerId, 'colic-coverage', projectColicCoverages);
}

/** Copy for the honest empty on a real contact. */
export const NO_COLIC_COVERAGE_COPY = 'No colic coverage enrolled yet';

/** The four program confirmations, none recorded, for a real contact with no coverage record. */
export const REAL_ENROLLMENT_STEPS_NONE: EnrollmentStep[] = [
  { index: 1, title: 'Application details', detail: 'Not recorded', state: 'pending' },
  { index: 2, title: 'Written acceptance', detail: 'Not recorded', state: 'pending' },
  { index: 3, title: 'Qualifying period', detail: 'Not started', state: 'pending' },
  { index: 4, title: 'Active coverage', detail: 'Not confirmed', state: 'pending' },
];

/** Same four steps when HubSpot holds a coverage record; this view never confirms a step itself. */
export const REAL_ENROLLMENT_STEPS_ON_FILE: EnrollmentStep[] = REAL_ENROLLMENT_STEPS_NONE.map((step) => ({
  ...step,
  detail: 'On the HubSpot coverage record; not confirmed in this workspace',
}));

/** String-valued coverage properties worth showing (HubSpot system `hs_*` keys excluded). */
function displayableProperties(properties: Record<string, unknown>, max = 8): Array<[string, string]> {
  return Object.entries(properties)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim().length > 0 && !entry[0].startsWith('hs_'))
    .slice(0, max);
}

export function ColicCoverageReviewScreen(
  props: ColicCoverageReviewScreenProps = {},
): ReactNode {
  const params = useParams();
  const navigate = useNavigate();
  const customerId = params.customerId || SARAH.id;
  const identity = useReviewIdentity(customerId);
  const isPreview = identity.isPreview;
  const firstName = identity.firstName;
  const horses = useContactHorses(customerId);
  const [activeHorseId, setActiveHorseId] = useState(DUKE_COVERAGE.id);
  const active = COVERAGE_HORSES.find((h) => h.id === activeHorseId) ?? DUKE_COVERAGE;

  const openHorseProfile = () => {
    navigate(`/horses/${customerId}`);
  };
  const openSubscription = () => {
    navigate(`/subscriptions/${customerId}`);
  };

  // R10: fold the live Kixie mid-call signal into companion state. The
  // test seam (companionSignals.midCall) still wins so unit tests stay
  // hermetic.
  const liveMidCall = useActiveCallSignal({
    customerId,
    label: `Call in progress with ${isPreview ? SARAH.firstName : firstName}`,
    actionName: 'KIXIE_CALL_IN_PROGRESS',
  });

  // W35: the real coverage read. 'skipped' in preview / dev-mock (same
  // gate as the identity and horses hooks), so the Duke fixture is never
  // mixed with a real read. Its loading / error / empty states render
  // inline in the coverage card (not through the boundary) so the real
  // horses list stays visible whatever the coverage route returns.
  const coverage = useColicCoverage(customerId);
  // W35.1: the "Related subscription" aside reads the W34 subscription
  // summary (same hook and labels as the Subscriptions tab). 'skipped' in
  // preview / dev-mock, where the CoverageSidebar fixture renders instead.
  const subscription = useSubscriptionSummary(customerId);

  const mergedSignals = useMemo(
    () => mergeMidCallSignal(props.companionSignals, liveMidCall),
    [props.companionSignals, liveMidCall],
  );
  const state = useMemo<BoundaryState>(
    // Explicit companionSignals (and the live mid-call signal) keep the
    // test seams deterministic; otherwise the default layout renders.
    () => resolveCompanionState(mergedSignals),
    [mergedSignals],
  );

  if (!isPreview) {
    // W32: real session. Real horses, inert controls, no fixture.
    // W35: real coverage records (0 today) with an honest empty.
    const namedHorses = horses.items.filter((h) => typeof h.name === 'string' && h.name.trim().length > 0);
    const records = coverage.status === 'ready' ? coverage.data ?? [] : [];
    const coveragePill =
      coverage.status === 'loading'
        ? 'Reading'
        : coverage.status === 'error'
          ? 'Not read'
          : records.length === 0
            ? 'Not enrolled'
            : `${records.length} on file`;
    const whatHeading =
      coverage.status === 'ready' && records.length > 0 ? 'Coverage record on file' : 'No enrollment on file yet';
    const whyNowBody =
      coverage.status === 'loading'
        ? 'Reading Colic Coverage records from HubSpot.'
        : coverage.status === 'error'
          ? `The Colic Coverage read failed (${coverage.errorCode ?? 'ERR'}). Active coverage is not confirmed.`
          : records.length > 0
            ? `${records.length} Colic Coverage record${records.length === 1 ? '' : 's'} on file in HubSpot. Active coverage is confirmed only on the record itself, never from a purchase.`
            : 'No Colic Coverage record is on file for this contact in HubSpot. Active coverage is not confirmed.';
    // W35.1: related subscription from the W34 summary. 'skipped' cannot
    // happen here (a real session is exactly what un-skips the hook), but
    // it is mapped to the loading copy rather than to a fixture.
    const subscriptionCard = subscriptionCardState(subscription);
    const subscriptionPill =
      subscriptionCard.kind === 'on-file'
        ? subscriptionCard.facts.status
        : subscriptionCard.kind === 'none'
          ? 'None on file'
          : subscriptionCard.kind === 'error'
            ? 'Not read'
            : 'Reading';
    return (
      <WorkspaceChrome screen="colic-coverage" rail="my-work">
        <div className="breadcrumbs">
          <div>
            My work
            <span className="slash">/</span>
            <strong>{identity.customer.fullName}</strong>
          </div>
          {identity.statusPill ? <span className="preview">{identity.statusPill}</span> : null}
        </div>
        <PriorityFocus
          ariaLabel="COLIC COVERAGE · NEXT STEP"
          eyebrow="COLIC COVERAGE · NEXT STEP"
          heading={`Review ${firstName}'s Colic Coverage`}
          what={{ label: 'WHAT', heading: whatHeading, iconId: 'message' }}
          whyNow={{ label: 'WHY NOW', body: whyNowBody, iconId: 'clock' }}
          nextAction={{
            label: 'NEXT ACTION',
            body: 'Ask about the horse and the qualifying subscription on the call; enrollment stays paused until the write gate opens.',
            detail: 'Verify acceptance separately from purchase.',
            iconId: 'target',
          }}
          ownership={{ label: 'OWNERSHIP', iconId: 'people', detail: identity.ownerDetail }}
          owner={identity.owner}
        />
        <IdentityRow customer={identity.customer} badgeLabel={identity.badgeLabel} sinceLabel={identity.sinceLabel} />
        <FileShortcuts customerId={customerId} firstName={identity.customer.firstName} />
        <CustomerTabs customerId={customerId} active="colic-coverage" includeServiceNeeds />

        <DataBoundary state={state} testId="colic-coverage-boundary">
          <div className="coverage-grid">
            <div className="coverage-left">
              <section className="card" data-testid="colic-real-coverage" style={{ padding: 24 }}>
                <div className="section-heading">
                  <h3>Coverage record</h3>
                  <span className="needs-pill">{coveragePill}</span>
                </div>
                {coverage.status === 'loading' ? (
                  <p role="status">Loading colic coverage…</p>
                ) : coverage.status === 'error' ? (
                  <p role="alert" data-testid="colic-real-error">
                    Colic coverage could not load ({coverage.errorCode ?? 'ERR'}
                    {coverage.httpStatus ? `, HTTP ${coverage.httpStatus}` : ''}).
                    {coverage.errorDetail ? ` ${coverage.errorDetail}` : ''}
                  </p>
                ) : records.length === 0 ? (
                  <div data-testid="colic-real-empty">
                    <p><strong>{NO_COLIC_COVERAGE_COPY}</strong></p>
                    <p>
                      No Colic Coverage application, acceptance, qualifying period or active coverage is recorded for
                      {' '}{firstName} in HubSpot. Coverage is never confirmed from a purchase alone.
                    </p>
                  </div>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 18 }} data-testid="colic-real-records">
                    {records.map((record, index) => {
                      const shown = displayableProperties(record.properties);
                      return (
                        <li key={record.coverageId || `coverage-${index}`} data-testid="colic-real-record">
                          <strong>Coverage record {record.coverageId || `#${index + 1}`}</strong>
                          {` · ${record.horses.length} horse${record.horses.length === 1 ? '' : 's'} linked`}
                          {shown.length > 0 ? (
                            <dl style={{ margin: '6px 0 0', fontSize: 11 }}>
                              {shown.map(([key, value]) => (
                                <div key={key}>
                                  <dt style={{ display: 'inline', fontWeight: 700 }}>{key}: </dt>
                                  <dd style={{ display: 'inline', margin: 0 }}>{value}</dd>
                                </div>
                              ))}
                            </dl>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="plan-controls" data-testid="colic-real-controls">
                  <button className="blue-action" type="button" disabled aria-describedby="colic-gate-note">Start enrollment</button>
                  <button className="outlined" type="button" disabled aria-describedby="colic-gate-note">Update application</button>
                  <button className="outlined" type="button" disabled aria-describedby="colic-gate-note">Record acceptance</button>
                  <p id="colic-gate-note">Paused by the live-write gate</p>
                </div>
              </section>
              <EnrollmentSteps steps={records.length > 0 ? REAL_ENROLLMENT_STEPS_ON_FILE : REAL_ENROLLMENT_STEPS_NONE} />
            </div>
            <aside className="coverage-right">
              <section className="card" data-testid="colic-real-horses" style={{ padding: 24 }}>
                <div className="section-heading">
                  <h3>Horses on this account</h3>
                  <span>HubSpot contact</span>
                </div>
                {horses.status === 'loading' ? (
                  <p role="status">Loading horses…</p>
                ) : horses.status === 'error' ? (
                  <p role="alert">Horses could not load ({horses.errorCode || 'ERR'}).</p>
                ) : namedHorses.length === 0 ? (
                  <p>No horse on file yet. Ask during the call and add it on the Horses tab.</p>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {namedHorses.map((h) => (
                      <li key={h.id ?? h.name ?? ''} data-testid="colic-real-horse">
                        <strong>{h.name}</strong>
                        {h.age ? ` · ${h.age} yrs` : ''}
                        {h.gender ? ` · ${h.gender}` : ''}
                        {' · No coverage record'}
                      </li>
                    ))}
                  </ul>
                )}
                <button className="text-btn" type="button" onClick={openHorseProfile} style={{ marginTop: 12 }}>
                  Open horse profiles &rarr;
                </button>
              </section>
              <section className="card" style={{ padding: 24 }} data-testid="colic-real-subscription" data-subscription-state={subscriptionCard.kind}>
                <div className="section-heading">
                  <h3>Related subscription</h3>
                  <span className={subscriptionCard.kind === 'on-file' ? 'teal-status' : 'needs-pill'}>{subscriptionPill}</span>
                </div>
                {subscriptionCard.kind === 'error' ? (
                  <p role="alert" data-testid="colic-real-subscription-error">
                    {SUBSCRIPTION_CARD_ERROR_TITLE} ({describeSubscriptionCardError(subscriptionCard)}).
                    {subscriptionCard.detail ? ` ${subscriptionCard.detail}` : ''}
                  </p>
                ) : subscriptionCard.kind === 'none' ? (
                  <div data-testid="colic-real-subscription-empty">
                    <p><strong>{SUBSCRIPTION_CARD_NONE}</strong></p>
                    <p>
                      HubSpot holds no Recharge subscription status for {firstName}, so no qualifying supply can be
                      read from this workspace.
                    </p>
                  </div>
                ) : subscriptionCard.kind === 'on-file' ? (
                  <div data-testid="colic-real-subscription-facts">
                    <p><strong>{subscriptionCard.facts.product}</strong></p>
                    <p data-testid="colic-real-subscription-line">
                      {subscriptionCard.facts.cadence} · Next charge {subscriptionCard.facts.nextCharge} · {subscriptionCard.facts.status}
                    </p>
                    <p>
                      Qualifying supply not verified. Confirm the applicable program requirements before representing
                      this plan as qualifying.
                    </p>
                  </div>
                ) : (
                  <p role="status">{SUBSCRIPTION_CARD_LOADING}</p>
                )}
                <button className="text-btn" type="button" onClick={openSubscription}>
                  Review subscription &rarr;
                </button>
              </section>
            </aside>
          </div>
        </DataBoundary>

        <UpNext customerId={customerId} />
        <ActionFooter
          disabled={true}
          noteWhenDisabled="No enrollment loaded for this contact"
          noteWhenEnabled="Ready to continue"
          summaryBody="Keep the customer's enrollment follow-up assigned."
        />
      </WorkspaceChrome>
    );
  }

  return (
    <WorkspaceChrome screen="colic-coverage" rail="my-work">
      <div className="breadcrumbs">
        <div>
          My work
          <span className="slash">/</span>
          <strong>{SARAH.fullName}</strong>
        </div>
        <span className="preview">Design preview &middot; Sample enrollment status</span>
      </div>
      <PriorityFocus
        ariaLabel="COLIC COVERAGE · NEXT STEP"
        eyebrow="COLIC COVERAGE · NEXT STEP"
        heading={`Review ${active.name}'s enrollment`}
        what={{
          label: 'WHAT',
          heading: `Complete ${active.name}'s enrollment details`,
          iconId: 'message',
        }}
        whyNow={{
          label: 'WHY NOW',
          body: `${active.name}'s application needs more information. Active coverage is not confirmed.`,
          iconId: 'clock',
        }}
        nextAction={{
          label: 'NEXT ACTION',
          body: 'Complete the missing details and review enrollment requirements.',
          detail: 'Verify acceptance separately from purchase.',
          iconId: 'target',
        }}
        ownership={{
          label: 'OWNERSHIP',
          iconId: 'people',
          detail: 'Primary account owner. Coverage preserves ownership.',
        }}
      />
      <IdentityRow customer={SARAH} badgeLabel="Enrollment needs information" />
      <FileShortcuts customerId={customerId} firstName={SARAH.firstName} />
      <CustomerTabs customerId={customerId} active="colic-coverage" includeServiceNeeds />

      <DataBoundary state={state} testId="colic-coverage-boundary">
        <div className="coverage-grid">
          <div className="coverage-left">
            <CoverageRecord horse={active} onViewHorseProfile={openHorseProfile} />
            <AttentionList items={active.attention} />
            <EnrollmentSteps steps={active.enrollmentSteps} />
          </div>
          <CoverageSidebar
            horses={COVERAGE_HORSES}
            activeHorseId={activeHorseId}
            onSelectHorse={setActiveHorseId}
            onReviewSubscription={openSubscription}
          />
        </div>
      </DataBoundary>

      <UpNext customerId={customerId} />
      <ActionFooter
        disabled={true}
        noteWhenDisabled="Enrollment review"
        noteWhenEnabled="Ready to continue"
        summaryBody="Keep the customer's enrollment follow-up assigned."
      />
    </WorkspaceChrome>
  );
}

export default ColicCoverageReviewScreen;
